import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { LeftOutlined } from '../../components/icons'
import { Tabs } from '../../components/ui/Tabs'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import { combinedItemCount, useCartCount } from '../../lib/cart/cart-count-context'
import { formatOrderRef } from '../checkout/checkout-domain'
import { ActionBar } from './ActionBar'
import { CustomsPanel } from './CustomsPanel'
import { OrderSummaryCard, OrderSummaryCardSkeleton } from './OrderSummaryCard'
import { ShipmentsPanel } from './ShipmentsPanel'
import { StatusStepper, StatusStepperSkeleton } from './StatusStepper'
import type { OrderDetailData } from './domain'

type LoadState = 'loading' | 'loaded' | 'not-found' | 'error'

/**
 * Order detail screen (`/orders/:orderId`, UX spec §2). Fetches the full
 * `Order` aggregate via `GET /api/orders/{orderId}` (ADR-0036 §4, gated to
 * `order:read`). Renders the action bar, summary card, order-level status
 * stepper, and the Shipments/Customs tabs — everything that IS backed by
 * real `Order` data renders from it directly. Customs (`order.customs`) and
 * the order activity log (`order.activityLog`, OrderSummaryCard's "View
 * activity log") are real data (UID-121 S7, ADR-0046).
 *
 * UID-123 (S8 design-fidelity round 2, item 4): the standalone "Per-line
 * tracking" table and the page-level Documents section were removed —
 * neither has a counterpart in the design (`Fictiv Platform.dc.html`'s
 * Shipments tab only ever renders one card per shipment, each with its own
 * inline parts table + document/drawing links). That content now lives
 * inside `ShipmentsPanel`'s per-shipment cards; see its doc comment for the
 * line↔shipment↔document mapping and the one real, disclosed UI gap
 * (thumbnail images).
 */
export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [order, setOrder] = useState<OrderDetailData | undefined>()
  const [liveMessage, setLiveMessage] = useState('')
  const [activeTab, setActiveTab] = useState('shipments')
  const [reordering, setReordering] = useState(false)
  const panelHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()

  const load = useCallback(async () => {
    if (!orderId) return
    setLoadState('loading')
    setLiveMessage(`Loading order ${orderId}.`)
    try {
      const { data, error } = await apiClient.GET('/orders/{orderId}', {
        params: { path: { orderId } },
      })
      if (data) {
        setOrder(data)
        setLoadState('loaded')
        setLiveMessage(`Order ${orderId} loaded.`)
        return
      }
      const status = (error as { status?: number } | undefined)?.status
      if (status === 401) {
        return // Handled globally by authMiddleware's session invalidation.
      }
      if (status === 404) {
        setLoadState('not-found')
      } else {
        setLoadState('error')
      }
      setLiveMessage(`Order ${orderId} failed to load.`)
    } catch {
      setLoadState('error')
      setLiveMessage(`Order ${orderId} failed to load.`)
    }
  }, [orderId])

  useEffect(() => {
    void load()
  }, [load])

  /** Reorder this order into the cart (UID-94): POST the seam, then refresh the top-bar
   * cart badge from the authoritative line-count the ack returns + toast the outcome. */
  const handleReorder = useCallback(async () => {
    if (!orderId) return
    setReordering(true)
    try {
      const { data, error } = await apiClient.POST('/orders/{orderId}/reorder', {
        params: { path: { orderId } },
      })
      if (error || !data) {
        const title =
          typeof error === 'object' && error !== null && 'title' in error
            ? String((error as { title?: unknown }).title)
            : undefined
        showToast(title ?? 'We couldn’t reorder this order. Try again.', 'error')
        return
      }
      // The reorder ack's `lineCount` counts part lines only; push the COMBINED
      // count (lines + requested services) from the authoritative cart so the
      // badge doesn't undercount a cart that also carries requested services.
      const refreshed = await apiClient.GET('/cart')
      setLineCount(refreshed.data ? combinedItemCount(refreshed.data) : data.lineCount)
      showToast(data.message ?? 'Reorder started; review it in your cart.', 'success')
      // Land the buyer on the cart so they see the re-added lines (UID-94 follow-up).
      navigate('/cart')
    } finally {
      setReordering(false)
    }
  }, [orderId, showToast, setLineCount, navigate])

  useEffect(() => {
    if (loadState === 'loaded') {
      panelHeadingRef.current?.focus()
    }
  }, [loadState])

  if (!orderId) {
    return null
  }

  return (
    <section aria-labelledby="order-detail-heading">
      {/* Visually hidden: the prototype's own top bar (`<!-- action bar -->`,
      ~L3259-3268) has no separate "Order {id}" page title — the order's
      identity is the big "Order ID" stat inside OrderSummaryCard below,
      matching the mock exactly. This heading stays in the a11y tree (not
      `display:none`) purely for document/heading-navigation structure. */}
      <h1 id="order-detail-heading" className="sr-only">
        Order {formatOrderRef(orderId)} (ID {orderId})
      </h1>
      <p aria-live="polite" className="sr-only">
        {liveMessage}
      </p>

      {loadState === 'loading' && (
        <>
          <OrderSummaryCardSkeleton />
          <StatusStepperSkeleton />
        </>
      )}

      {loadState === 'not-found' && (
        <div role="alert" className="table-error-panel">
          <p>
            We couldn&rsquo;t find this order. It may have been removed, or the link may be
            incorrect.
          </p>
          <Link to="/orders" className="btn-link">
            Back to Orders
          </Link>
        </div>
      )}

      {loadState === 'error' && (
        <div role="alert" className="table-error-panel">
          <p>Couldn&rsquo;t load this order.</p>
          <button type="button" className="btn-link" onClick={load}>
            Try again
          </button>
          <Link to="/orders" className="btn-link">
            Back to Orders
          </Link>
        </div>
      )}

      {loadState === 'loaded' && order && (
        <>
          <div className="order-detail-topbar">
            <Link to="/orders" className="back-link">
              <LeftOutlined aria-hidden="true" /> Back to all orders
            </Link>
            <ActionBar
              order={order}
              onReorder={() => void handleReorder()}
              reordering={reordering}
            />
          </div>
          <OrderSummaryCard order={order} />
          <StatusStepper order={order} />

          <Tabs
            className="order-detail-tabs"
            items={[
              {
                id: 'shipments',
                // UID-212 follow-up: the design renders the shipment count INLINE in the label as
                // "Shipments (N)" (`Fictiv Platform.dc.html:3342`) — a parenthetical, NOT the blue
                // rounded `.tab-count` pill (that shared badge stays for the other screens that use
                // it). So the count rides in the label text here and no count badge is set.
                // UID-317 review follow-up: a placed order ALWAYS shows at least one shipment —
                // ShipmentsPanel synthesizes a "Preparing" card when `order.shipments` is empty
                // (buildInitialShipment / synthesizePreparingShipment), so the tab count must match
                // that displayed minimum of 1 rather than reading a bare 0 from an empty array.
                label: `Shipments (${order.shipments?.length || 1})`,
              },
              // UID-232: the Customs tab shows on EVERY order, beside Shipments (reversing the
              // earlier import-only gate on `order.customs != null`). Placement now attaches an
              // open, editable customs record to every order, and CustomsPanel renders its own
              // empty-state for any legacy order that predates that change and still has no record.
              {
                id: 'customs',
                label: 'Customs information',
                // UID-123 item 5: the design shows this warning badge
                // unconditionally, regardless of submission status
                // (`Fictiv Platform.dc.html:3342`).
                badge: { kind: 'warning' },
              },
            ]}
            activeId={activeTab}
            onChange={setActiveTab}
            aria-label="Order detail sections"
          >
            {(id) => (
              <>
                <h2 className="sr-only" ref={panelHeadingRef} tabIndex={-1}>
                  {id === 'shipments' ? 'Shipments' : 'Customs information'}
                </h2>
                {id === 'shipments' ? (
                  <ShipmentsPanel order={order} />
                ) : (
                  <CustomsPanel order={order} onOrderChange={setOrder} />
                )}
              </>
            )}
          </Tabs>
        </>
      )}
    </section>
  )
}
